import { t } from '@web/i18n';
import { AppIcons, Button } from '@web/ui/core';

import { VollWordmark } from '../components';
import { useAuthLogin } from '../hooks';
import { fireAndForget } from '@web/ui/core/utils';

/*
 * Two-column login matching VOLL Login v2.html.
 * Left: terracotta brand panel with editorial serif tagline.
 * Right: paper card with eyebrow, headline, lede, SSO button.
 */
export function LoginPage(): React.JSX.Element {
  const { signIn, pending, error } = useAuthLogin();
  const handleClick = (): void => {
    const returnTo = new URLSearchParams(globalThis.location.search).get('returnTo') ?? undefined;
    fireAndForget(signIn({ returnTo }));
  };

  return (
    <main aria-label="01 Login" className="grid min-h-screen grid-cols-1 md:grid-cols-[1.05fr_1fr]">
      <section
        aria-label="VOLL"
        className="relative isolate flex min-h-auth-panel flex-col justify-between overflow-hidden bg-primary px-7 py-9 text-primary-foreground md:min-h-0 md:px-16 md:py-14"
      >
        <span aria-hidden className="pointer-events-none absolute inset-0 -z-10 login-brand-bg" />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-4 rounded-md border login-frame md:inset-6"
        />

        <header className="flex items-center gap-2.5 font-mono text-card-id uppercase tracking-eyebrow-wider font-medium text-login-header">
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full bg-primary-foreground login-dot-halo"
          />
          <span>{t('auth.brand.os')}</span>
        </header>

        <div className="flex max-w-prose-lg flex-col gap-8">
          <div aria-label="VOLL">
            <VollWordmark />
          </div>

          <h1 className="text-display text-login-tagline m-0 text-display-md leading-display-sm text-balance md:text-display-xl md:leading-display-xl tracking-display-condensed">
            {t('auth.brand.tagline.lead')}
            <em className="not-italic text-login-tagline-em">{t('auth.brand.tagline.em')}</em>
          </h1>

          <p className="m-0 max-w-prose-md text-card-body leading-[1.55] text-login-sub text-pretty">
            {t('auth.brand.tagline.sub')}
          </p>
        </div>

        <footer className="flex items-center justify-between font-mono text-card-id font-medium uppercase tracking-eyebrow-wider text-login-footer">
          <span>{t('auth.brand.copyright')}</span>
        </footer>
      </section>

      <section
        aria-label={t('auth.login.submitAria')}
        className="relative flex items-center justify-center bg-card px-7 py-10 md:px-14 md:py-14"
      >
        <span aria-hidden className="pointer-events-none absolute inset-0 login-form-bg" />

        <div className="relative flex w-full max-w-form-md flex-col gap-7">
          <span className="inline-flex items-center gap-2 text-eyebrow">
            <span
              aria-hidden
              className="inline-flex h-4 w-4 items-center justify-center rounded-sm border border-input text-faint-foreground"
            >
              <AppIcons name="lock" size={9} />
            </span>
            {t('auth.login.eyebrow')}
          </span>

          <h2 className="text-display m-0 text-4xl leading-display-md md:text-display-xl md:leading-display-lg tracking-display-condensed">
            {t('auth.login.title.lead')}
            <em className="not-italic">{t('auth.login.title.em')}</em>
          </h2>

          <p className="-mt-3 m-0 text-card-body leading-[1.55] text-muted-foreground text-pretty">
            {t('auth.login.lede')}
          </p>

          <Button variant="outline" size="lg" onClick={handleClick} disabled={pending}>
            <span>{pending ? t('auth.login.sso.redirecting') : t('auth.login.sso.cta')}</span>
            <span className="ml-auto font-mono text-micro font-normal text-muted-foreground">
              {t('auth.login.sso.hint')}
            </span>
          </Button>

          {error ? (
            <p role="alert" className="-mt-3 text-meta leading-tight text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      </section>
    </main>
  );
}
